import React from 'react';
import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { FormattedMessage, IntlShape } from 'react-intl';
import {
    ArtifactStatus,
    ArtifactsTypeForBatchRequest,
    ArtifactType,
    IArtifactDTO,
    IArtifactsDAO,
    IInitialArtifactDTO,
    IInitialArtifactsListDTO,
    IMeetingArtifactDTO,
    IRequestArtifactsPayload,
    IUpdateArtifactStatusPayload,
    IUpdateArtifactSubscriptionPayload,
    RecordingType,
    ResolutionMp4,
    TRequestArtifactsTab
} from '../../../../api/artifacts.dao';
import { IErrorParsing } from '../../../../infrastructure/error-parsing';
import { alertServerError, showNotification } from '../../../global/modal/alerts-host/alert-templates';
import { IEventDetailsDTO } from '../../../../api/events.dao';
import { IAppsyncSubscription, subscribeAppsyncData } from '../../../../infrastructure/utils/appsync.utils';
import { onUpdateAssetMetadata } from '../../../../graphql/subscriptions';
import RequestArtifactsPopoverViewModel from './request-artifacts-popover/request-artifacts-popover.viewmodel';
import { IAlertsService } from '../../../../infrastructure/alerts';
import { Space } from '../../../../constants/html-character-entities';
import { ARTIFACTS_PAGE_TRACKING_EVENTS } from '../../../../tracking-service/tracking-values';
import TrackingServiceProvider from '../../../../tracking-service/tracking-service-provider';
import { artifactMp4RecordingTypes, passportReplayStatusLabel } from '../../../../constants/artifacts.constants';
import PublishArtifactPopoverVM from './publish-artifact-popover/publish-artifact-popover.viewmodel';
import KnovioPreviewLinkPopoverVM, { KnovioPreviewSourceType } from '../shared/knovio-preview-link-popover/knovio-preview-link-popover.viewmodel';
import { hasPermission } from '../../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../../api/roles.dao';
import { NOT_AVAILABLE_STRING } from '../../../../constants/common.constants';
import { KnovioLabel } from '../../../../api/connections.dao';

class ArtifactsViewModel {
    @mobx.observable
    public isMeetingArtifactsLoading: boolean = false;
    @mobx.observable
    private meetingsArtifactsList: IMeetingArtifactDTO[] = [];
    @mobx.observable
    public event: IEventDetailsDTO;
    private subscriptions: Set<IAppsyncSubscription> = new Set();
    @mobx.observable
    public isRequestArtifactsModalOpen = false;
    @mobx.observable
    public requestArtifactsPopoverVM: RequestArtifactsPopoverViewModel;
    @mobx.observable
    public publishArtifactPopoverVM: PublishArtifactPopoverVM;
    private knovioPreviewLinkPopoverVM = new KnovioPreviewLinkPopoverVM(KnovioPreviewSourceType.Artifact);

    public constructor(
        private readonly _event: IEventDetailsDTO,
        public readonly intl: IntlShape,
        @IoC.Inject private readonly artifactsDAO = IoC.Container.get(IArtifactsDAO),
        @IoC.Inject private readonly alertsService: IAlertsService = IoC.Container.get(IAlertsService),
        @IoC.Inject private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),
    ) {
        this.event = this._event;
    }

    private static handleApiException = (e: Error, title: string = 'Error'): void => {
        if (e) alertServerError(title, IoC.Container.get(IErrorParsing).extractMessage(e));
    };

    public get loading(): boolean { return this.isMeetingArtifactsLoading; }
    public get meetingsArtifacts(): IMeetingArtifactDTO[] { return this.meetingsArtifactsList; }

    public initialize = async (): Promise<void> => {
        await this.fetchMeetingsArtifacts(true);
        this.initPublishArtifactPopoverVM();
    };

    public getKnovioPreviewLinkPopoverVM = (): KnovioPreviewLinkPopoverVM => {
        return this.knovioPreviewLinkPopoverVM;
    };

    public onOpenKnovioPreviewLinkPopover = (meetingId: number, assetMetadataId: number) => {
        const isKnovioPreviewPermitted = hasPermission(Permission.PREVIEW_KNOVIO_CONNECTION);
        if (!isKnovioPreviewPermitted) return;
        this.knovioPreviewLinkPopoverVM.setConnectionInfo({
            assetMetadataId: assetMetadataId,
            eventId: this.event.eventId,
            accountId: this.event.accountId,
            meetingId: meetingId,
        });
        this.knovioPreviewLinkPopoverVM.openPopover();
    };

    @mobx.action
    public fetchMeetingsArtifacts = async (showLoadingState: boolean): Promise<void> => {
        if (showLoadingState) {
            mobx.runInAction(() => {
                this.isMeetingArtifactsLoading = true;
            });
        }

        try {
            const result = await this.artifactsDAO.getAllArtifacts({
                accountId: this.event.accountId,
                eventId: this.event.eventId,
            });
            this.onFetchMeetingsArtifactsSuccess(result);
        } catch (error) {
            ArtifactsViewModel.handleApiException(error);
        } finally {
            mobx.runInAction(() => {
                this.isMeetingArtifactsLoading = false;
            });
        }
    };

    @mobx.action
    private onFetchMeetingsArtifactsSuccess = (data: IInitialArtifactsListDTO): void => {
        this.meetingsArtifactsList = this.convertArtifactsIntoMeetingArtifactsDTO(data.content);
        this.initArtifactSubscriptions(data.content);
    };

    public cancelArtifactSubscriptions(): void {
        this.subscriptions.forEach(subscription => subscription?.unsubscribe());
        this.subscriptions.clear();
    }

    private initArtifactSubscriptions(artifacts: IInitialArtifactDTO[]): void {
        artifacts.forEach(artifact => {
            if (artifact.status === ArtifactStatus.PENDING) {
                this.subscribeToArtifactStatusUpdate(artifact.assetMetadataId);
            }
        });
    }

    private async subscribeToArtifactStatusUpdate(assetMetadataId: number): Promise<void> {
        const subscription = await subscribeAppsyncData<IUpdateArtifactSubscriptionPayload>(
            onUpdateAssetMetadata,
            { assetMetadataId: assetMetadataId },
            data => {
                const { value: { data: { onUpdateAssetMetadata: updatePayload } } } = data;
                this.updateArtifactStatusAndUnsubscribe(updatePayload, subscription);
            },
        );
        this.subscriptions.add(subscription);
    }

    private updateArtifactStatusAndUnsubscribe(artifactStatusPayload: IUpdateArtifactStatusPayload, subscription: IAppsyncSubscription): void {
        const isUpdated = this.updateArtifactValues(artifactStatusPayload);
        if (isUpdated) {
            subscription?.unsubscribe();
            this.subscriptions.delete(subscription);
        }
    }

    @mobx.action
    private updateArtifactValues(artifactStatusPayload: IUpdateArtifactStatusPayload): boolean {
        const {
            assetMetadataId,
            status,
            recordingType,
            resolution,
        } = artifactStatusPayload;
        const id = Number(assetMetadataId);
        const newStatus = this.setArtifactValueByKey(id, 'assetStatus', status);
        this.setArtifactValueByKey(id, 'recordingType', recordingType);
        this.setArtifactValueByKey(id, 'resolution', resolution);

        return newStatus;
    }

    @mobx.action
    public onDownloadSingleArtifact = async (assetMetadataId: number, assetName: string): Promise<void> => {
        try {
            await this.artifactsDAO.downloadSingleArtifact({
                accountId: this.event.accountId,
                eventId: this.event.eventId,
                assetMetadataId: assetMetadataId,
            }, assetName);

            this.trackDownloadArtifactMixPanel(assetMetadataId);
        } catch (error) {
            ArtifactsViewModel.handleApiException(error);
        }
    };

    public getMeetingAndArtifactByArtifactId(assetMetadataId: number): {meeting: IMeetingArtifactDTO; artifact: IArtifactDTO} {
        for (const meeting of this.meetingsArtifactsList) {
            const { artifacts } = meeting;
            for (const artifact of artifacts) {
                if (artifact.assetMetadataId === assetMetadataId) {
                    return {meeting: meeting, artifact: artifact};
                }
            }
        }
    };

    public trackDownloadArtifactMixPanel = (assetMetadataId: number): void => {
        const { meeting, artifact } = this.getMeetingAndArtifactByArtifactId(assetMetadataId);

        this.trackingService.get().track(ARTIFACTS_PAGE_TRACKING_EVENTS.DOWNLOAD_ARTIFACT, {
            ...this.getMixinAccountNameAndEventId(),
            'Meeting ID': meeting.meetingId || 'null',
            'Artifact Type': artifact.assetType ,
        });
    };

    private convertArtifactsIntoMeetingArtifactsDTO = (artifactsList: IInitialArtifactDTO[]): IMeetingArtifactDTO[] => {
        const meetingArtifacts = artifactsList.reduce((acc, cur) => {
            const meetingInTheList = acc.find(meeting => meeting?.meetingId === cur.meetingId);
            if (meetingInTheList) {
                const meetingArtifact: IArtifactDTO = {
                    assetMetadataId: cur.assetMetadataId,
                    assetStatus: cur.status,
                    assetType: cur.assetType,
                    note: cur.note,
                    assetName: cur.assetName,
                    publicDownloadLink: cur.publicDownloadLink,
                    recordingType: cur.recordingType,
                    resolution: cur.resolution
                };
                meetingInTheList.artifacts.push(meetingArtifact);
            } else {
                acc.push(this.convertArtifactIntoMeetingArtifact(cur));
            }

            return acc;
        }, [] as IMeetingArtifactDTO[]);
        const deletedMeeting = meetingArtifacts.find(m => !m.meetingId);
        return !deletedMeeting ? meetingArtifacts : [...meetingArtifacts.filter(m => !!m.meetingId), deletedMeeting];
    };

    private convertArtifactIntoMeetingArtifact = (artifact: IInitialArtifactDTO): IMeetingArtifactDTO => ({
        artifacts: [{
            assetMetadataId: artifact.assetMetadataId,
            assetStatus: artifact.status,
            assetType: artifact.assetType,
            note: artifact.note,
            assetName: artifact.assetName,
            publicDownloadLink: artifact.publicDownloadLink,
            recordingType: artifact.recordingType,
            resolution: artifact.resolution
        }],
        meetingId: artifact.meetingId,
        presenters: artifact.presenters,
        startDateTime: artifact.startDateTime,
        endDateTime: artifact.endDateTime,
        topic: artifact.topic,
        type: artifact.type,
    });


    @mobx.action
    public onDeleteArtifact = (assetMetadataId: number, meetingId: number, assetType: ArtifactType): void => {
        this.alertsService.confirm({
            message: (
                <span className='alert-message'>
                    <FormattedMessage
                        id='oe.confirm.areYouSureTo'
                        description='Confirm message'
                        defaultMessage='Are you sure you want to'
                    />
                    <Space />
                    <span className='alert-message--danger'>
                        <FormattedMessage
                            id='oe.artifacts.deleteThisArtifact'
                            description='Confirm message'
                            defaultMessage='Delete this Artifact'
                        />
                    </span>
                    ?<Space />
                    {assetType === ArtifactType.PASSPORT_REPLAY
                        ? <FormattedMessage
                            id='oe.artifacts.artifactNoLongerBeAvailable'
                            description='Confirm message'
                            defaultMessage={`This will permanently delete both the artifact and the Passport Replay connection.
                                The replay will no longer be available on OE Passport. The ${KnovioLabel.OEStream} 
                                will not be deleted, you will be able to find it in the ${KnovioLabel.OEStream} system.`}
                        />
                        : <FormattedMessage
                            id='oe.artifacts.onesDeleted'
                            description='Confirm message'
                            defaultMessage='Once deleted, the file cannot be recovered, but it can be requested again.'
                        />
                    }
                </span>
            ),
            confirmButtonProps: {
                text: (
                    <FormattedMessage
                        id='oe.artifacts.confirmDelete'
                        description='label text'
                        defaultMessage='Yes, Delete'
                    />
                ),
                modifier: 'confirm-warn',
                handler: () => this.onDeleteSingleArtifact(assetMetadataId, meetingId, assetType)
            },
            cancelButtonProps: {
                modifier: 'cancel'
            }
        });
    };

    @mobx.action
    public onDeleteSingleArtifact = async (assetMetadataId: number, meetingId: number, assetType: ArtifactType): Promise<void> => {
        try {
            await this.artifactsDAO.deleteSingleArtifact({
                accountId: this.event.accountId,
                eventId: this.event.eventId,
                assetMetadataId: assetMetadataId,
            });
            this.fetchMeetingsArtifacts(true);
            this.trackDeleteArtifactMixPanel(meetingId, assetType);
        } catch (error) {
            ArtifactsViewModel.handleApiException(error);
        }
    };

    public getMixinAccountNameAndEventId = (): { [key: string]: string | number } => ({
        'Account Name': this.event.accountName,
        'Account ID': this.event.accountId,
        'Event ID': this.event.eventId,
        'Event Name': this.event.name
    });

    private trackDeleteArtifactMixPanel = (meetingId: number, assetType: ArtifactType): void => {
        this.trackingService.get().track(ARTIFACTS_PAGE_TRACKING_EVENTS.DELETE_ARTIFACT, {
            ...this.getMixinAccountNameAndEventId(),
            'Meeting ID': meetingId?.toString() || '',
            'Artifact Type': assetType as string
        });
    };

    @mobx.action
    public requestMeetingsArtifactsInBatch = async (payload: IRequestArtifactsPayload): Promise<void> => {
        try {
            showNotification(this.intl.formatMessage({
                id: 'oe.artifacts.generateStarted',
                description: 'Notification about changing artifacts status',
                defaultMessage: 'Artifacts generation has started',
            }));
            this.closeRequestArtifactsModal();
            await this.artifactsDAO.requestMeetingsArtifactsInBatch({
                accountId: this.event.accountId,
                eventId: this.event.eventId,
                ...payload
            });
            this.handleTrackingEvent(payload);
            this.reFetchMeetingsArtifacts(false);
        } catch (error) {
            ArtifactsViewModel.handleApiException(error);
        }
    };

    @mobx.action
    public openRequestArtifactsModal = (): void => {
        this.requestArtifactsPopoverVM = new RequestArtifactsPopoverViewModel(
            {
                timeZone: this.event.timeZone,
                eventId: this.event.eventId,
                accountId: this.event.accountId,
            },
            this.intl,
            this.requestMeetingsArtifactsInBatch,
        );
        this.isRequestArtifactsModalOpen = true;
    };

    @mobx.action
    public closeRequestArtifactsModal = (): void => {
        this.isRequestArtifactsModalOpen = false;
        this.requestArtifactsPopoverVM = null;
    };

    private reFetchMeetingsArtifacts = (withReloadData: boolean): void => {
        this.cancelArtifactSubscriptions();
        this.fetchMeetingsArtifacts(withReloadData);
    };

    @mobx.action.bound
    public updateArtifactNotesById(id: number, note: string): void {
        this.setArtifactValueByKey(id, 'note', note);
    }

    @mobx.action.bound
    public async createArtifactDownloadLink(assetMetadataId: number, downloadLinkCreatedCb: (publicLink?: string) => void): Promise<void> {
        try {
            const { publicDownloadLink } = await this.artifactsDAO.createArtifactLink({
                eventId: this.event.eventId,
                accountId: this.event.accountId,
                assetMetadataId: assetMetadataId
            });

            this.setArtifactPublicDownloadLinkById(assetMetadataId, publicDownloadLink);
            downloadLinkCreatedCb(publicDownloadLink);
        } catch (error) {
            ArtifactsViewModel.handleApiException(error);
            downloadLinkCreatedCb();
        }
    }

    @mobx.action
    public onDeleteArtifactDownloadLink = (assetMetadataId: number): void => {
        this.alertsService.confirm({
            message: (
                <span className='alert-message'>
                    <FormattedMessage
                        id='oe.confirm.areYouSureTo'
                        description='Confirm message'
                        defaultMessage='Are you sure you want to'
                    />
                    <Space />
                    <span className='alert-message--danger'>
                        <FormattedMessage
                            id='oe.artifacts.deleteThisLink'
                            description='Confirm message'
                            defaultMessage='Delete this link'
                        />
                    </span>
                    ?<Space /><FormattedMessage
                        id='oe.artifacts.linkNoLongerDownload'
                        description='Alert message'
                        defaultMessage='Once deleted, the link will no longer download the file,
                            and this cannot be undone, but a new link can be generated.'
                    />
                </span>
            ),
            confirmButtonProps: {
                text: (
                    <FormattedMessage
                        id='oe.artifacts.confirmDelete'
                        description='label text'
                        defaultMessage='Yes, Delete'
                    />
                ),
                modifier: 'confirm-warn',
                handler: () => this.onDeleteSingleArtifactDownloadLink(assetMetadataId)
            },
            cancelButtonProps: {
                modifier: 'cancel'
            }
        });
    };

    @mobx.action
    public onDeleteSingleArtifactDownloadLink = async (assetMetadataId: number): Promise<void> => {
        try {
            await this.artifactsDAO.deleteArtifactLink({
                accountId: this.event.accountId,
                eventId: this.event.eventId,
                assetMetadataId: assetMetadataId,
            });
            this.fetchMeetingsArtifacts(true);
        } catch (error) {
            ArtifactsViewModel.handleApiException(error);
        }
    };

    @mobx.action
    private setArtifactPublicDownloadLinkById(id: number, link: string): void {
        this.setArtifactValueByKey(id, 'publicDownloadLink', link);
    }

    @mobx.action
    private setArtifactValueByKey(id: number, key: keyof IArtifactDTO, value: IArtifactDTO[keyof IArtifactDTO]): boolean {
        for (const meetingArtifacts of this.meetingsArtifactsList) {
            const { artifacts } = meetingArtifacts;
            for (const artifact of artifacts) {
                if (artifact.assetMetadataId === id) {
                    artifact[key as string] = value;
                    return true;
                }
            }
        }
        return false;
    }

    @mobx.action
    public onPublishPassportReplay = (assetMetadataId: number, meetingId: number): void => {
        this.alertsService.confirm({
            message: (
                <span className='alert-message'>
                <FormattedMessage
                    id='oe.confirm.areYouSureTo'
                    description='Confirm message'
                    defaultMessage='Are you sure you want to'
                />
                <Space />
                <span className='alert-message--danger'>
                    <FormattedMessage
                        id='oe.artifacts.publishThisArtifact'
                        description='Confirm message'
                        defaultMessage='Publish this Artifact'
                    />
                </span>
                ?<Space />
                <FormattedMessage
                    id='oe.artifacts.publishArtifactWillUpdateBothStatuses'
                    description='Alert message'
                    defaultMessage={`Publishing the Artifact will update both the Artifact status and the ${KnovioLabel.OEStream} 
                    Connection status to Published. The Replay will go live on Passport for Attendees to view.`}
                />
                </span>
            ),
            confirmButtonProps: {
                text: (
                    <FormattedMessage
                        id='oe.artifacts.confirmPublish'
                        description='label text'
                        defaultMessage='Yes, Publish'
                    />
                ),
                modifier: 'confirm-safe',
                handler: () => this.publishSinglePassportReplay(assetMetadataId, meetingId)
            },
            cancelButtonProps: {
                modifier: 'cancel'
            }
        });
    };

    @mobx.action
    public publishSinglePassportReplay = async (assetMetadataId: number, meetingId: number): Promise<void> => {
        try {
            await this.artifactsDAO.publishPassportReplay({
                accountId: this.event.accountId,
                eventId: this.event.eventId,
                assetMetadataId: assetMetadataId,
            });
            this.fetchMeetingsArtifacts(true);
            this.trackPublishSinglePassportReplayMixPanel(meetingId);
            showNotification(this.intl.formatMessage({
                id: 'oe.artifacts.publishPassportReplaySuccessful',
                description: 'Notification text',
                defaultMessage: 'Replay Connection was successfully published',
            }));
        } catch (error) {
            ArtifactsViewModel.handleApiException(error);
        }
    };

    private handleTrackingEvent = (payload: IRequestArtifactsPayload) => {
        const trackingStrategy: Record<TRequestArtifactsTab, (data: IRequestArtifactsPayload[keyof IRequestArtifactsPayload]) => void> = {
            artifacts: (data: IRequestArtifactsPayload['artifacts']) => {
                const recordingTypes = artifactMp4RecordingTypes(this.intl);
                const resolutionMp4 = data.resolution === null ? ResolutionMp4.DEFAULT : data.resolution;
                const formatMp4 = data.recordingType === null
                    ? recordingTypes.find(r => r.value === RecordingType.DEFAULT).label
                    : recordingTypes.find(r => r.value === data.recordingType).label
                ;
                const isMP4Selected = data.artifactTypes.find(item => item === ArtifactsTypeForBatchRequest.MP4);

                this.trackingService.get().track(ARTIFACTS_PAGE_TRACKING_EVENTS.GENERATE_ARTIFACTS_IN_BATCH, {
                    ...this.getMixinAccountNameAndEventId(),
                    'Meeting IDs': data.meetingIds.join(', '),
                    'Artifact Types': data.artifactTypes.join(', '),
                    'MP4 Resolution': isMP4Selected ? resolutionMp4 : NOT_AVAILABLE_STRING,
                    'MP4 Format': isMP4Selected ? formatMp4 : NOT_AVAILABLE_STRING,
                });
            },
            replays: (data: IRequestArtifactsPayload['replays']) => {
                this.trackingService.get().track(ARTIFACTS_PAGE_TRACKING_EVENTS.GENERATE_PASSPORT_REPLAYS, {
                    ...this.getMixinAccountNameAndEventId(),
                    'Meeting IDs': data.meetingIds.join(', '),
                    'Artifact Status': data.reviewRequired
                        ? passportReplayStatusLabel(this.intl).REVIEW_REQUIRED
                        : passportReplayStatusLabel(this.intl).PUBLISH_AUTOMATICALLY
                });
            }
        };

        for (const key in payload) {
            if (payload.hasOwnProperty(key)) {
                trackingStrategy[key](payload[key]);
            }
        }
    };

    public trackPublishSinglePassportReplayMixPanel = (meetingId: number): void => {
        this.trackingService.get().track(ARTIFACTS_PAGE_TRACKING_EVENTS.PUBLISH_ARTIFACT, {
            ...this.getMixinAccountNameAndEventId(),
            'Meeting ID': meetingId || 'null',
        });
    };

    @mobx.action
    private initPublishArtifactPopoverVM = (): void => {
        this.publishArtifactPopoverVM = new PublishArtifactPopoverVM(
            this.intl,
            this.onPublishPassportReplay
        );
    };

    @mobx.action
    public openPublishArtifactPopover = (assetMetadataId: number, meetingId: number): void => {
        const selectedMeetingArtifact = this.meetingsArtifactsList.find(m => m.meetingId === meetingId);
        this.publishArtifactPopoverVM.setMeetingArtifact(selectedMeetingArtifact, assetMetadataId);
    };
}

export default ArtifactsViewModel;
